import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';

import { DearDocFooter } from '@/components/DearDocFooter';
import { Loader } from '@/components/Loader';
import { useConnection } from '@/hooks/useConnection';
import { useGoogleActionsConversion } from '@/hooks/useGoogleActionsConversion';
import { useProviders } from '@/hooks/useProviders';
import { TIME_SLOT_NO_LONGER_AVAILABLE_MESSAGE } from '@/lib/errors';
import { hasAllInfoFromQuery } from '@/lib/helpers/urlParams.ts';
import { BookingApp } from '@/modules/BookingApp';
import { BookingLayout } from '@/modules/BookingLayout';
import { ConfirmationScreen } from '@/modules/ConfirmationScreen';
import { PatientInformationForm } from '@/modules/PatientInformationForm';
import { createAppointment } from '@/services/createAppointment';
import { useBookingStore } from '@/store/useBookingStore';
import { usePracticeStore } from '@/store/usePracticeStore';
import { CustomerData } from '@/types/customer.types';

import styles from './FullBookingPage.module.css';

export const FullBookingPage = () => {
  const { freeSlots } = usePracticeStore();
  const { provider, appointmentType, slot, clearSelection, onAppointmentCreated } =
    useBookingStore();
  const [showForm, setShowForm] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { handleConversionEvent } = useGoogleActionsConversion();
  const hasQueryParams = hasAllInfoFromQuery();
  const { isFetching: isFetchingConnection, connection } = useConnection();
  const { isFetching: isFetchingProviders } = useProviders();

  const location = useLocation();

  // Extract requiredFields from connection
  const requiredFields = connection?.requiredFields || ['name', 'phone', 'email'];

  // Extract query params
  const queryParams = new URLSearchParams(location.search);
  const prefillValues = {
    name: queryParams.get('name') || '',
    email: queryParams.get('email') || '',
    phone: queryParams.get('phone') || '',
    date_of_birth: queryParams.get('date_of_birth') || '',
  };

  const {
    isPending,
    error,
    data: newAppointment,
    mutate,
  } = useMutation({
    mutationFn: (customer: CustomerData) =>
      createAppointment(provider, appointmentType, slot, freeSlots, customer),
  });

  useEffect(() => {
    if (newAppointment?.confirmed) {
      setShowConfirmation(true);
      onAppointmentCreated(newAppointment);
      clearSelection();
      closeFoldOut();
      if (import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID) {
        ReactGA.event({
          category: 'appointment',
          action: 'appointment-confirmed',
        });
      }
    }
  }, [newAppointment, clearSelection, onAppointmentCreated]);

  useEffect(() => {
    if (error?.message) {
      toast.error(error.message);
    }
  }, [error]);

  useEffect(() => {
    if (!newAppointment?.error) {
      return;
    }
    const errMessage = newAppointment?.error?.toString();
    if (errMessage === TIME_SLOT_NO_LONGER_AVAILABLE_MESSAGE) {
      hidePatientForm();
    }
    toast.error(errMessage);
  }, [newAppointment?.error]);

  const closeFoldOut = () => {
    setTimeout(() => {
      window.parent.postMessage(
        JSON.stringify({ action: 'foldOutClose', data: { type: 'iframe' } }),
        '*',
      );
    }, 7500);
  };

  const showPatientForm = () => {
    setShowForm(true);
  };

  const hidePatientForm = () => {
    setShowForm(false);
  };

  const handleSubmit = async (customer: CustomerData) => {
    if (!customer) {
      console.error('Missing patient info');
      return;
    }
    try {
      if (import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID) {
        ReactGA.event({
          category: 'appointment',
          action: 'appointment-requested',
        });
      }
      mutate(customer);
      await handleConversionEvent();
    } catch (err) {
      console.error(err);
    }
  };

  if (isPending || isFetchingConnection || isFetchingProviders) {
    return (
      <div className={styles.loaderContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <BookingLayout>
      <div className={styles.pageContent}>
        <span className={styles.pageTitle}>
          {hasQueryParams ? 'Schedule your appointment' : 'Online Booking App'}
        </span>
        <div className={styles.pageSectionContainer}>
          {showConfirmation ? (
            <ConfirmationScreen />
          ) : showForm ? (
            <PatientInformationForm
              goBack={hidePatientForm}
              callback={handleSubmit}
              requiredFields={requiredFields}
              prefillValues={prefillValues}
            />
          ) : (
            <BookingApp callback={showPatientForm} />
          )}
        </div>
        <div className={styles.footerContainer}>
          <DearDocFooter />
        </div>
      </div>
    </BookingLayout>
  );
};
